import React from 'react';
import { Building2, Heart, ShoppingCart, GraduationCap, TrendingUp, Users, Clock, DollarSign, Award, Target, BarChart3, Briefcase } from 'lucide-react';

export default function CasosDeUso() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center py-12 md:py-20"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-allti-blue/90 via-gray-900/85 to-allti-blue/90"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-allti-blue/20 backdrop-blur-sm px-3 md:px-4 py-1.5 md:py-2 rounded-full border border-allti-blue/30 mb-4 md:mb-6">
              <Award className="w-3 h-3 md:w-4 md:h-4 text-allti-blue" />
              <span className="text-xs md:text-sm font-medium text-white">Resultados Comprobados</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
              Casos de Éxito que<br />
              <span className="text-allti-blue">Transforman Negocios</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-6 md:mb-8 max-w-3xl">
              Descubre cómo hemos ayudado a empresas de múltiples industrias a superar sus desafíos tecnológicos 
              con soluciones innovadoras basadas en HPE.
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 md:p-4 border border-white/20">
                <div className="flex items-center gap-2 text-allti-blue mb-1 md:mb-2">
                  <Briefcase className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="text-xl md:text-2xl font-bold text-white">8+</span>
                </div>
                <p className="text-xs md:text-sm text-gray-200">Industrias Atendidas</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 md:p-4 border border-white/20">
                <div className="flex items-center gap-2 text-hpe-green mb-1 md:mb-2">
                  <BarChart3 className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="text-xl md:text-2xl font-bold text-white">40%</span>
                </div>
                <p className="text-xs md:text-sm text-gray-200">ROI Promedio</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 md:p-4 border border-white/20">
                <div className="flex items-center gap-2 text-yellow-400 mb-1 md:mb-2">
                  <Target className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="text-xl md:text-2xl font-bold text-white">98%</span>
                </div>
                <p className="text-xs md:text-sm text-gray-200">Éxito de Proyectos</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 md:p-4 border border-white/20">
                <div className="flex items-center gap-2 text-allti-blue mb-1 md:mb-2">
                  <Users className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="text-xl md:text-2xl font-bold text-white">150+</span>
                </div>
                <p className="text-xs md:text-sm text-gray-200">Casos Exitosos</p>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <button className="bg-allti-blue text-white px-6 md:px-8 py-2.5 md:py-3 rounded-lg hover:bg-blue-600 transition shadow-lg shadow-allti-blue/50 text-sm md:text-base">
                Explorar Casos por Industria
              </button>
              <button className="bg-white/10 backdrop-blur-sm text-white px-6 md:px-8 py-2.5 md:py-3 rounded-lg hover:bg-white/20 transition border border-white/30 text-sm md:text-base">
                Solicitar Consultoría
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Rest of the page */}
      <CasosSection />
    </div>
  );
}

function CasosSection() {
  const casos = [
    {
      id: 1,
      icon: Building2,
      sector: 'Banca y Finanzas',
      empresa: 'Banco Nacional',
      desafio: 'Necesitaban modernizar su core bancario y mejorar la disponibilidad del servicio para soportar 10 millones de transacciones diarias.',
      solucion: 'Implementamos servidores HPE ProLiant Gen10 Plus con almacenamiento HPE Nimble y HPE GreenLake para gestión híbrida.',
      resultados: [
        { icon: TrendingUp, texto: '99.999% de disponibilidad' },
        { icon: Clock, texto: '70% reducción en tiempo de respuesta' },
        { icon: DollarSign, texto: '35% ahorro en costos operativos' }
      ],
      imagen: 'https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg'
    },
    {
      id: 2,
      icon: Heart,
      sector: 'Salud',
      empresa: 'Hospital Regional del Sur',
      desafio: 'Requerían centralizar historiales médicos electrónicos de 5 clínicas con acceso seguro y en tiempo real para 500+ médicos.',
      solucion: 'Diseñamos una infraestructura con HPE SimpliVity para hiperconvergencia, respaldo integrado y HPE Aruba para conectividad segura.',
      resultados: [
        { icon: Users, texto: 'Acceso instantáneo para 500+ usuarios' },
        { icon: Clock, texto: 'Backup automático cada 15 minutos' },
        { icon: TrendingUp, texto: '100% cumplimiento HIPAA' }
      ],
      imagen: 'https://images.unsplash.com/photo-1506399558188-acca6f8cbf41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxkYXRhJTIwY2VudGVyfGVufDB8fHx8MTc2MTA3MTgyMXww&ixlib=rb-4.1.0&q=85'
    },
    {
      id: 3,
      icon: ShoppingCart,
      sector: 'Retail',
      empresa: 'Cadena Comercial MegaMart',
      desafio: 'Enfrentaban caídas del sistema en temporada alta (Black Friday) con 200 tiendas y e-commerce afectado.',
      solucion: 'Implementamos HPE GreenLake con escalabilidad automática, servidores HPE Synergy y almacenamiento flash HPE Alletra.',
      resultados: [
        { icon: TrendingUp, texto: 'Zero downtime en temporada alta' },
        { icon: DollarSign, texto: '45% reducción en CapEx' },
        { icon: Clock, texto: '3x más rápido procesamiento de pagos' }
      ],
      imagen: 'https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg'
    },
    {
      id: 4,
      icon: GraduationCap,
      sector: 'Educación',
      empresa: 'Universidad Tecnológica Nacional',
      desafio: 'Migración a clases virtuales para 15,000 estudiantes con infraestructura obsoleta y capacidad limitada.',
      solucion: 'Desplegamos HPE GreenLake con modelo de pago por uso, servidores virtualizados y plataforma de colaboración en la nube.',
      resultados: [
        { icon: Users, texto: '15,000 usuarios concurrentes' },
        { icon: DollarSign, texto: '50% reducción presupuestal TI' },
        { icon: TrendingUp, texto: 'Escalabilidad flexible según semestre' }
      ],
      imagen: 'https://images.unsplash.com/photo-1506399558188-acca6f8cbf41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxkYXRhJTIwY2VudGVyfGVufDB8fHx8MTc2MTA3MTgyMXww&ixlib=rb-4.1.0&q=85'
    }
  ];

  return (
    <div className="bg-white">

      {/* Casos de Uso */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {casos.map((caso, index) => {
              const Icon = caso.icon;
              return (
                <div 
                  key={caso.id}
                  className="bg-white rounded-2xl shadow-xl overflow-hidden"
                  data-testid={`caso-${caso.id}`}
                >
                  <div className={`grid grid-cols-1 lg:grid-cols-2 gap-0 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                    <div className={`p-10 lg:p-12 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="bg-allti-blue/10 w-14 h-14 rounded-lg flex items-center justify-center">
                          <Icon className="text-allti-blue" size={28} />
                        </div>
                        <div>
                          <p className="text-sm text-allti-blue font-semibold uppercase tracking-wide">{caso.sector}</p>
                          <h3 className="text-2xl font-bold text-gray-900">{caso.empresa}</h3>
                        </div>
                      </div>

                      <div className="space-y-6">
                        <div>
                          <h4 className="text-lg font-bold text-gray-900 mb-2">El Desafío</h4>
                          <p className="text-gray-600">{caso.desafio}</p>
                        </div>

                        <div>
                          <h4 className="text-lg font-bold text-gray-900 mb-2">Nuestra Solución</h4>
                          <p className="text-gray-600">{caso.solucion}</p>
                        </div>

                        <div>
                          <h4 className="text-lg font-bold text-gray-900 mb-4">Resultados Obtenidos</h4>
                          <div className="space-y-3">
                            {caso.resultados.map((resultado, idx) => {
                              const ResultIcon = resultado.icon;
                              return (
                                <div key={idx} className="flex items-center space-x-3">
                                  <div className="bg-hpe-green/10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <ResultIcon className="text-hpe-green" size={20} />
                                  </div>
                                  <span className="text-gray-700 font-medium">{resultado.texto}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <img 
                        src={caso.imagen}
                        alt={caso.empresa}
                        className="w-full h-full object-cover min-h-[400px]"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sectores */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Sectores que Atendemos</h2>
            <p className="text-xl text-gray-600">
              Experiencia comprobada en múltiples industrias
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Building2, nombre: 'Banca y Finanzas' },
              { icon: Heart, nombre: 'Salud' },
              { icon: ShoppingCart, nombre: 'Retail' },
              { icon: GraduationCap, nombre: 'Educación' },
            ].map((sector, idx) => {
              const SectorIcon = sector.icon;
              return (
                <div key={idx} className="text-center">
                  <div className="bg-white w-20 h-20 rounded-xl shadow-lg flex items-center justify-center mx-auto mb-4 hover:shadow-xl transition-shadow">
                    <SectorIcon className="text-allti-blue" size={36} />
                  </div>
                  <p className="font-semibold text-gray-900">{sector.nombre}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-allti-blue to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            ¿Quieres ser nuestro próximo caso de éxito?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Cuéntanos tu desafío y diseñaremos la solución perfecta para tu empresa
          </p>
          <a 
            href="/contacto" 
            data-testid="casos-cta-btn"
            className="bg-white text-allti-blue px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors text-lg font-semibold inline-block"
          >
            Iniciar tu Proyecto
          </a>
        </div>
      </section>
    </div>
  );
}